import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { Sparkles, ChevronRight, MapPin } from "lucide-react";
import { formatNZD } from "@/lib/format";
import { buildAdPath } from "@/lib/slugs";

const FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=400&q=60";

/**
 * Homepage "Featured" rail — Paid Boost showcase.
 * Spec: SPEC_PAID_BOOST_ALL_PODS · GET /classifieds/featured?country=NZ&limit=12
 * Hidden when the rail is empty.
 */
export default function FeaturedRail() {
  const q = useQuery({
    queryKey: ["featured-rail"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/featured", {
        params: { country: "NZ", limit: 12 },
      });
      return data.listings || [];
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });

  const items = q.data || [];
  if (q.isLoading || items.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 md:px-8 py-4"
      data-testid="home-featured-rail"
    >
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-orange)]/10 text-[color:var(--brand-orange)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> Featured
          </div>
          <h2 className="mt-2 font-display text-xl md:text-2xl font-black text-slate-900">
            Boosted on the homepage
          </h2>
        </div>
      </div>

      <div className="grid grid-flow-col auto-cols-[minmax(220px,240px)] md:auto-cols-[minmax(240px,260px)] gap-4 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth snap-x snap-mandatory">
        {items.map((l) => (
          <Link
            key={l.id}
            to={buildAdPath(l)}
            data-testid={`featured-card-${l.id}`}
            className="group block bg-white rounded-2xl shadow-card hover:shadow-card-hover overflow-hidden snap-start card-lift"
          >
            <div className="relative aspect-[4/3] bg-slate-100">
              <img
                src={l.thumb || l.image || l.images?.[0] || FALLBACK}
                alt={l.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-orange)] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 shadow">
                <Sparkles className="w-3 h-3" /> Featured
              </span>
            </div>
            <div className="p-3">
              <div className="text-[15px] font-black text-slate-900 tracking-tight">
                {l.price_type === "contact" ? "Contact for price" : formatNZD(l.price)}
              </div>
              <h3 className="mt-1 font-display font-semibold text-sm text-slate-800 leading-snug clamp-2 min-h-[2.4rem] group-hover:text-[color:var(--brand-blue)] transition-colors">
                {l.title}
              </h3>
              <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                <span className="clamp-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {l.city || l.region || "New Zealand"}
                </span>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
